/**
 * KinkyMugs predictive search controller.
 * Fetches Shopify-rendered HTML from the predictive search section and injects it below the input.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.cache = new Map();
    this.debounceTimer = null;

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleInput = this.handleInput.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('.predictive-search__input');
    this.results = this.querySelector('.predictive-search__results');
    this.resultsInner = this.querySelector('.predictive-search__results-inner');
    this.loadingText = this.dataset.loadingText || 'Searching...';
    this.errorText = this.dataset.errorText || 'Something went wrong. Please try again.';
    this.sectionId = this.dataset.sectionId || 'predictive-search';
    this.fallbackSectionId = this.dataset.fallbackSectionId || 'predictive-search-fallback';

    if (!(this.input instanceof HTMLInputElement) || !(this.results instanceof HTMLElement) || !(this.resultsInner instanceof HTMLElement)) {
      return;
    }

    this.input.addEventListener('input', this.handleInput);
    this.input.addEventListener('focus', this.handleFocus);
    this.input.addEventListener('keydown', this.handleEscape);
    document.addEventListener('click', this.handleDocumentClick);
  }

  disconnectedCallback() {
    if (this.input instanceof HTMLInputElement) {
      this.input.removeEventListener('input', this.handleInput);
      this.input.removeEventListener('focus', this.handleFocus);
      this.input.removeEventListener('keydown', this.handleEscape);
    }

    document.removeEventListener('click', this.handleDocumentClick);
  }

  handleInput() {
    window.clearTimeout(this.debounceTimer);

    const query = this.input.value.trim();

    if (query.length < 2) {
      this.clearResults();
      return;
    }

    this.debounceTimer = window.setTimeout(() => {
      this.search(query).catch((error) => {
        if (error.name === 'AbortError') return;

        console.error(error);
        this.renderMessage(this.errorText, 'predictive-search__no-results');
      });
    }, 300);
  }

  handleFocus() {
    const query = this.input.value.trim();

    if (query.length >= 2 && this.resultsInner.childElementCount > 0) {
      this.open();
    }
  }

  handleEscape(event) {
    if (event.key !== 'Escape') return;
    this.close();
  }

  handleDocumentClick(event) {
    if (event.target instanceof Node && !this.contains(event.target)) {
      this.close();
    }
  }

  async search(query) {
    const normalizedQuery = query.trim();

    if (normalizedQuery.length < 2) {
      this.clearResults();
      return;
    }

    if (this.cache.has(normalizedQuery)) {
      this.renderHTML(this.cache.get(normalizedQuery));
      return;
    }

    if (this.abortController instanceof AbortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.renderLoading();
    this.open();

    const predictiveHTML = await this.fetchPredictiveResults(normalizedQuery);

    if (this.input.value.trim() !== normalizedQuery) {
      return;
    }

    if (this.hasResults(predictiveHTML)) {
      this.cache.set(normalizedQuery, predictiveHTML);
      this.renderHTML(predictiveHTML);
      return;
    }

    const fallbackHTML = await this.fetchStorefrontFallback(normalizedQuery);

    if (this.input.value.trim() !== normalizedQuery) {
      return;
    }

    const finalHTML = this.hasResults(fallbackHTML) ? fallbackHTML : predictiveHTML;
    this.cache.set(normalizedQuery, finalHTML);
    this.renderHTML(finalHTML);
  }

  async fetchPredictiveResults(query) {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '4',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      'resources[options][unavailable_products]': 'last',
      'options[prefix]': 'last',
      section_id: this.sectionId,
    });

    return this.fetchSectionMarkup(`search/suggest?${params.toString()}`);
  }

  async fetchStorefrontFallback(query) {
    const params = new URLSearchParams({
      q: query,
      type: 'product',
      'options[prefix]': 'last',
      section_id: this.fallbackSectionId,
    });

    return this.fetchSectionMarkup(`search?${params.toString()}`);
  }

  async fetchSectionMarkup(path) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
      signal: this.abortController.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Predictive search failed with status ${response.status}.`);
    }

    return response.text();
  }

  renderLoading() {
    this.resultsInner.innerHTML = `
      <div class="predictive-search__loading" aria-live="polite">
        <span class="predictive-search__loading-dot"></span>
        <span>${this.escapeHTML(this.loadingText)}</span>
      </div>
    `;
  }

  renderMessage(message, className) {
    this.resultsInner.innerHTML = `<p class="${className}">${this.escapeHTML(message)}</p>`;
    this.open();
  }

  renderHTML(html) {
    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(html, 'text/html');
    const renderedSection = documentFragment.querySelector('[data-predictive-search-section]');

    if (!(renderedSection instanceof HTMLElement)) {
      this.renderMessage(this.errorText, 'predictive-search__no-results');
      return;
    }

    this.resultsInner.innerHTML = renderedSection.innerHTML.trim();

    if (!this.resultsInner.innerHTML) {
      this.close();
      return;
    }

    this.open();
  }

  hasResults(html) {
    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(html, 'text/html');
    const renderedSection = documentFragment.querySelector('[data-predictive-search-section]');

    return renderedSection instanceof HTMLElement && renderedSection.dataset.hasResults === 'true';
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  clearResults() {
    this.resultsInner.innerHTML = '';
    this.close();
  }

  escapeHTML(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
